import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, X, Sparkles, Zap, BookOpen, Download, ShieldOff, Headphones } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const BENEFITS = [
  [ShieldOff, "Ad-free reading", "No interruptions, ever"],
  [BookOpen, "Premium-only stories", "Exclusive books & chapters"],
  [Zap, "Early access", "Read new chapters first"],
  [Sparkles, "Unlimited reading", "No chapter unlock limits"],
  [Download, "Book downloads", "Take stories offline (PDF/TXT)"],
  [Headphones, "Priority support", "We've got your back"],
];
export const FEATURE_LABELS = { ad_free: "Ad-free reading", premium_books: "Premium-only books", downloads: "Book downloads" };
export const money = (amt, cur = "usd") => {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur.toUpperCase() }).format(amt); }
  catch { return `${amt} ${cur.toUpperCase()}`; }
};

function PlanCard({ p, onBuy, loading, premiumActive }) {
  return (
    <div className={`relative rounded-3xl border p-8 ${p.highlight ? "border-primary shadow-[0_0_40px_-10px_hsl(var(--primary)/0.4)]" : "border-border"} surface-2`} data-testid={`plan-card-${p.id}`}>
      {p.highlight && <span className="absolute -top-3 left-8 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-primary text-primary-foreground">Best Value</span>}
      <h3 className="font-display text-2xl font-bold">{p.name}</h3>
      {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}
      <p className="mt-3"><span className="text-4xl font-display font-bold" data-testid={`plan-price-${p.id}`}>{money(p.amount, p.currency)}</span>
        <span className="text-muted-foreground">/{p.interval_label || `${p.days} days`}</span></p>
      <ul className="mt-5 space-y-2 text-sm">
        {Object.entries(FEATURE_LABELS).map(([k, label]) => (
          <li key={k} className={`flex items-center gap-2 ${p.features?.[k] === false ? "text-muted-foreground line-through" : ""}`}>
            {p.features?.[k] === false ? <X className="h-4 w-4" /> : <Check className="h-4 w-4 text-primary" />} {label}
          </li>
        ))}
        {(p.perks || []).map((perk) => <li key={perk} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {perk}</li>)}
        <li className="flex items-center gap-2 text-muted-foreground"><Check className="h-4 w-4 text-primary" /> {p.days} days of access</li>
      </ul>
      <button onClick={() => onBuy(p.id)} disabled={!!loading || premiumActive} data-testid={`checkout-${p.id}`}
        className="w-full mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
        {premiumActive ? "Already Premium" : loading === p.id ? "Redirecting…" : "Go Premium"}
      </button>
    </div>
  );
}

export default function Premium() {
  const { user, refreshUser } = useApp();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState("");
  useEffect(() => { api.get("/premium/plans").then((r) => setPlans(r.data)); refreshUser(); }, []); // eslint-disable-line

  const premiumActive = user?.premium && (!user?.premium_expiry || new Date(user.premium_expiry) > new Date());

  const checkout = async (planId) => {
    if (!user) return nav("/login");
    if (loading) return;
    setLoading(planId);
    try {
      const { data } = await api.post("/premium/checkout", { plan: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(""); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-14 fade-in">
      <div className="text-center">
        <div className="h-16 w-16 rounded-2xl bg-primary/15 grid place-items-center mx-auto"><Crown className="h-8 w-8 text-primary" /></div>
        <h1 className="text-4xl md:text-5xl font-display font-bold mt-6">F&F <span className="text-primary">Premium</span></h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Unlock every story, remove every ad, and read without limits. Your next obsession is waiting.</p>
        {premiumActive && <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-primary/15 text-primary" data-testid="premium-active-badge"><Check className="h-4 w-4" /> You're a Premium member{user.premium_expiry ? ` · active until ${new Date(user.premium_expiry).toLocaleDateString()}` : ""}</div>}
      </div>

      <div className={`grid gap-6 mt-12 mx-auto ${plans.length > 2 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 max-w-3xl"}`}>
        {plans.map((p) => <PlanCard key={p.id} p={p} onBuy={checkout} loading={loading} premiumActive={premiumActive} />)}
      </div>
      {!plans.length && <p className="text-center text-muted-foreground mt-12" data-testid="no-plans">Premium plans are coming soon.</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
        {BENEFITS.map(([Icon, title, sub]) => (
          <div key={title} className="flex gap-3 p-5 rounded-2xl border border-border surface-2">
            <Icon className="h-6 w-6 text-primary shrink-0" />
            <div><h4 className="font-semibold">{title}</h4><p className="text-sm text-muted-foreground">{sub}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}
